import PropTypes from "prop-types";
import React, { Component } from "react";
import { dateTimeToStringMomentFromNow } from "components/Shared/Formatters";
import { checkForPreviewResults, submittalIsRequired, submitResultViewCurrent, submitInitiate } from "requests/homeowner/veros";
import moment from "moment-timezone";
import MSpinner from "components/Shared/MSpinner.tsx";
import MCard from "components/Shared/MCard.tsx";
import MTable from "components/Shared/MTable.tsx";
import SportsKabaddiIcon from "@mui/icons-material/SportsKabaddi";

class UcdpSubmittalForm extends Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      initialTimerDelay: 30,
      subsequentTimerInterval: 30,

      isFetchingPreviewResults: true,
      previewResults: null,

      isFetchingSubmitRequiredResults: true,
      submitRequiredResults: null,

      isFetchingSubmitResults: true,
      submitResults: null,
      submitPollingForResults: false,
      submitResultsIntervalHandle: null,
    };
    this.startCheckForPreviewResults = this.startCheckForPreviewResults.bind(this);
    this.onPreviewResultsAvailable = this.onPreviewResultsAvailable.bind(this);
    this.startSubmittalIsRequired = this.startSubmittalIsRequired.bind(this);
    this.onStartSubmittalIsRequired = this.onStartSubmittalIsRequired.bind(this);
    this.startSubmitInitiate = this.startSubmitInitiate.bind(this);
    this.stopSubmitResultsIntervalHandle = this.stopSubmitResultsIntervalHandle.bind(this);
    this.startCheckForSubmitResults = this.startCheckForSubmitResults.bind(this);
    this.onInitiateUCDPSubmittalSuccess = this.onInitiateUCDPSubmittalSuccess.bind(this);
    this.startSubmitResultViewCurrent = this.startSubmitResultViewCurrent.bind(this);
    this.onSubmitResultsAvailable = this.onSubmitResultsAvailable.bind(this);
    this.showSubmitButton = this.showSubmitButton.bind(this);
    this.mounted = { current: true };
  }

  componentDidMount() {
    this.startSubmittalIsRequired();
    this.startSubmitResultViewCurrent();
    this.startCheckForPreviewResults();
  }

  componentWillUnmount() {
    const { submitResultsIntervalHandle } = this.state;
    if (submitResultsIntervalHandle) {
      window.clearInterval(submitResultsIntervalHandle);
    }
    this.mounted.current = false;
  }

  /** @description: starts request for preview results  */
  startCheckForPreviewResults() {
    this.setState({
      isFetchingPreviewResults: true
    });
    checkForPreviewResults(this.props.controlNumber, this.onPreviewResultsAvailable, this.mounted);
  }
  /** @description: handles response with preview results  */
  onPreviewResultsAvailable(previewResults) {
    this.setState({
      isFetchingPreviewResults: false,
      previewResults: previewResults
    });
  }

  /** @description: starts request for submittal required settings  */
  startSubmittalIsRequired() {
    this.setState({
      isFetchingSubmitRequiredResults: false,
      submitRequiredResults: null
    });
    submittalIsRequired(this.props.controlNumber, this.onStartSubmittalIsRequired, this.mounted);
  }
  onStartSubmittalIsRequired(result) {
    this.setState({
      isFetchingSubmitRequiredResults: false,
      submitRequiredResults: result
    });
  }

  /** @description: starts request for UCDP submittal */
  startSubmitInitiate() {
    this.stopSubmitResultsIntervalHandle();
    this.setState({
      isFetchingSubmitResults: true,
      submitResults: null
    });
    submitInitiate(this.props.controlNumber, this.onInitiateUCDPSubmittalSuccess, this.mounted);
  }

  /** @description: stops background polling for UCDP results  */
  stopSubmitResultsIntervalHandle() {
    if (this.state.submitResultsIntervalHandle) {
      window.clearInterval(this.state.submitResultsIntervalHandle);
      this.setState({
        submitResultsIntervalHandle: null
      });
    }
  }
  /** @description: starts request for UCDP submit results */
  startCheckForSubmitResults() {
    if (!this.state.submitResultsIntervalHandle) {
      this.setState({
        submitResultsIntervalHandle: this.state.submitResultsIntervalHandle || window.setInterval(this.startSubmitResultViewCurrent, this.state.initialTimerDelay * 1000)
      });
    }
  }
  /** @description: handles response from initating UCDP submittal   */
  onInitiateUCDPSubmittalSuccess(result) {
    this.onSubmitResultsAvailable(result);
  }
  /** @description: starts request for UCDP submittal results settings  */
  startSubmitResultViewCurrent() {
    if (this.state.isFetchingSubmitResults && this.state.submitResults !== null) return;
    this.setState({
      isFetchingSubmitResults: true,
    });
    submitResultViewCurrent(this.props.controlNumber, this.onSubmitResultsAvailable, this.mounted);
  }
  /** @description: handles response from UCDP submittal results   */
  onSubmitResultsAvailable(result) {
    this.setState({
      isFetchingSubmitResults: false,
      submitResults: result
    });
    if (result) {
      this.setState({
        initialTimerDelay: result && result.pollingIntervalSecondsInitial || this.state.initialTimerDelay,
        subsequentTimerInterval: result && result.pollingIntervalSecondsSubsequent || this.state.subsequentTimerInterval,
      });
      if (result.resultReceivedDate) {
        // See if the current result is less than 5 minutes old
        const maxAge = moment.duration(5, "minutes");
        const resultAge = moment.duration(moment().diff(moment(result.resultReceivedDate)));
        if (resultAge < maxAge) {
          this.stopSubmitResultsIntervalHandle();
          window.setTimeout(this.startSubmitResultViewCurrent, 7000);
        }
      }
      if (result.requestInProgress) {
        this.startCheckForSubmitResults();
      } else {
        this.stopSubmitResultsIntervalHandle();
      }
    }
  }

  momentFromNow(momentValue) {
    return dateTimeToStringMomentFromNow(momentValue);
  }

  /** @description: figures out what submit options to show */
  showSubmitButton() {

    let showSubmitButton = true;
    let message = null;
    const { submitResults } = this.state;
  
    if (submitResults) {
      if (submitResults.requestInProgress) {
        showSubmitButton = false;
      }
    }
    return {
      label: "Send UCDP Submittal",
      showSubmitButton: showSubmitButton,
      message: message,
    };
  }

  render() {
    const {
      isFetchingPreviewResults,
      previewResults,
      isFetchingSubmitResults,
      submitResults,
    } = this.state;
    const previewColumns = [
      { name: "Result Code", dataSource: "resultCode" },
      { name: "Message", dataSource: "resultMessage" },
      { name: "Override Comment", dataSource: "selectedOverrideComment" },
      { name: "Resolved", dataSource: (row) => this.momentFromNow(row.resolvedDate) }
    ];
    const previewFields = (previewResults &&
      ([
        {
          control: <span>{previewResults.resultReceivedDate ?
            `Preview results received ${this.momentFromNow(previewResults.resultReceivedDate)}` :
            `Submit request was sent. Status last checked ${this.momentFromNow(previewResults.lastPolledDate)}`}</span>
        },
        {
          control: <MTable
            columnDefinitions={previewColumns}
            data={previewResults.items}
          />
        },
      ])) || [];

    const submitColumns = [
      { name: "Code", dataSource: "resultCode" },
      { name: "Action Message", dataSource: "resultMessage" },
      { name: "Section", dataSource: "formSection" },
      { name: "Field", dataSource: "formField" },
      { name: "Property Affected", dataSource: "dataPoint" },
      { name: "Override Request", dataSource: "selectedVerosOverrideTypeName" },
      { name: "Override Comment", dataSource: "selectedOverrideComment" },
      { name: "Override Date", dataSource: (row) => this.momentFromNow(row.resolvedDate) },
      { name: "Severity", dataSource: "verosSeverityTypeName" },
    ];

    let submitFields = (submitResults &&
      ([
        {
          control: <MTable
            columnDefinitions={submitColumns}
            data={submitResults.items || []}
          />
        },
      ])) || [{
        control: <MSpinner />
      }];
    if (!isFetchingSubmitResults && !submitResults) {
      submitFields = [];
    }

    let submitResultMessage = "";
    if (!isFetchingSubmitResults) {
      if (submitResults) {
        if (submitResults.resultReceivedDate) {
          submitResultMessage = `Submit results received ${this.momentFromNow(submitResults.resultReceivedDate)}`;
        } else {
          submitResultMessage = `Submit request sent ${this.momentFromNow(submitResults.createDate)} last checked ${this.momentFromNow(submitResults.lastPolledDate)} curent request status \"${submitResults.requestVerosStatusName}\"`;
          if (submitResults.resultVerosStatusName) {
            submitResultMessage += ` response status \"${submitResults.resultVerosStatusName}\"`;
          }
        }
        submitFields = [{ control: (<div>{submitResultMessage}</div>) }, ...submitFields];
        if (submitResults.requestFailed) {
          submitFields = [{ control: <div><h3>The last UCDP submittal failed</h3></div> }, ...submitFields];
        }
        if (submitResults.requestInProgress) {
          submitFields = [{ control: <div><h3>There is a UCDP submittal in progress</h3></div> }, ...submitFields];
        }
      } else {
        submitFields = [{ control: <div><h3>No UCDP submittal found</h3></div> }, ...submitFields];
      }
    } else {
      submitFields = [{ control: <div>Determining UCDP status</div> }, ...submitFields];
    }

    let submitButtonSettings = this.showSubmitButton();
    if (submitButtonSettings.message) {
      submitFields = [{
        control: <div>{submitButtonSettings.message}</div>
      },
      ...submitFields];
    }

    let submitActions = [];
    // Add the UCDP submit action button
    if (submitButtonSettings.showSubmitButton) {
      submitActions = [{
        icon: <SportsKabaddiIcon />,
        helpText: "Sends the MISMO to UCDP for submittal through Veros",
        children: <span> {submitButtonSettings.label}</span>,
        onClick: this.startSubmitInitiate,
      },
      ...submitActions];
    }

    return (
      <>
        {previewFields &&
          <MCard
            title="UCDP Preview Results"
            isEditable={false}
            disableHoverStyles={true}
            isFetching={isFetchingPreviewResults}
            fields={previewFields}
          />}
        <hr />
        {submitFields &&
          <MCard
            title="UCDP Submittal Results"
            isEditable={false}
            disableHoverStyles={true}
            fields={submitFields}
            actions={submitActions}
          />
        }
      </>
    );
  }
}

UcdpSubmittalForm.propTypes = {
  controlNumber: PropTypes.number.isRequired,
};
export default UcdpSubmittalForm;
